import React from "react";

// @mui material components
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import config from "config/config";

import PropTypes from "prop-types";

// Images
import tooltipIcon from "assets/images/icons/catering-icons/tooltip-icon.svg";
import { RESTAURANT_LEVEL_HEX_COLOR } from "../../../../../core/constant";
import { getRestaurantPageData } from "../../../_request";
import formatDateInTimezone from "core/utils/formatDate";
import formatNumberWithCommas from "../../../../../core/utils/formatNumberWithCommas";

const getRestaurantPageDetails = async (prefix) => {
  try {
    const {
      data: {
        success,
        data: { restaurantPageObj },
      },
    } = await getRestaurantPageData(prefix);
    if (success) {
      return restaurantPageObj.orderNowUrl;
    }
  } catch (err) {
    // silent — fallback URL used below
  }
};

const setplanDetails = (data, currentTier) => {
  let loyalty_plans_details = [];
  if (!data) return loyalty_plans_details;
  if (currentTier) {
    for (const plan of data) {
      if (plan.name === currentTier) {
        loyalty_plans_details = plan.loyalty_plans_details
          .slice()
          .sort((a, b) => a.cashbackMultiple_100 - b.cashbackMultiple_100);
        break;
      }
    }
  } else {
    for (const plan of data) {
      if (plan.name === "Level 1") {
        loyalty_plans_details = plan.loyalty_plans_details
          .slice()
          .sort((a, b) => a.cashbackMultiple_100 - b.cashbackMultiple_100);
        break;
      }
    }
  }
  return loyalty_plans_details;
};

export function buildProgressCell(progressData, currentLevel) {
  const progress = progressData?.progress || 0;
  return (
    <Tooltip
      key={"progress"}
      title={
        progressData?.hv
          ? currentLevel
            ? `$${formatNumberWithCommas(progressData.progressLeft ?? 0, { asCurrency: true })} left to reach next level above $${formatNumberWithCommas(
                progressData.hv / 100, { asCurrency: true }
              )}`
            : `$${formatNumberWithCommas(progressData.progressLeft ?? 0, { asCurrency: true })} needed to reach Level 1 (above $${formatNumberWithCommas(
                progressData.hv / 100, { asCurrency: true }
              )})`
          : currentLevel
          ? `Congratulations on reaching the highest level! You've spent a total of $${formatNumberWithCommas(
              (progressData?.totalOrderAmountInCents || 0) / 100, { asCurrency: true }
            )}. Enjoy the perks!`
          : "No qualifying orders yet"
      }
      placement="bottom"
    >
      <MDBox width="84px" textAlign="left">
        <MDBox
          sx={{
            backgroundColor: RESTAURANT_LEVEL_HEX_COLOR[currentLevel || "Not Eligible"],
            borderRadius: "4px",
            padding: "5px",
            position: "relative",
          }}
        >
          <MDBox
            sx={{
              height: "100%",
              width: `${100 - progress}%`,
              backgroundColor: "rgba(255, 255, 255, 0.65)",
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
            }}
          ></MDBox>
          <MDBox
            sx={{
              color: "#FFFFFF",
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "18px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            {currentLevel || "Not Eligible"}
          </MDBox>
        </MDBox>
      </MDBox>
    </Tooltip>
  );
}

export function buildMaxCashbackCell(loyaltyPlans, currentLevel, restaurantPrefix) {
  const planDetails = setplanDetails(loyaltyPlans, currentLevel);
  return (
    <MDBox sx={{ display: "flex", gap: 1 }}>
      <MDTypography
        variant="caption"
        color="text"
        fontWeight="medium"
        component="a"
        href={
          restaurantPrefix
            ? `${
                config[process.env.REACT_APP_ENV?.toUpperCase() || "LOCAL"]
                  .API_LANDING_PAGE_BASE_URL
              }/restaurants/${restaurantPrefix}`
            : undefined
        }
        target="_blank"
      >
        {!!planDetails && planDetails[planDetails.length - 1]?.cashbackMultiple_100
          ? planDetails[planDetails.length - 1]?.cashbackMultiple_100 / 100 + "X"
          : "-"}
      </MDTypography>
      <Tooltip
        key={"maxCashback"}
        title={
          <div style={{ textAlign: "left" }}>
            {planDetails.length === 1 ? (
              <div>
                {planDetails[0].minimumSaleInCents <= 100
                  ? `• Earn ${planDetails[0].cashbackMultiple_100 / 100}X rewards on all orders`
                  : `• Earn ${planDetails[0].cashbackMultiple_100 / 100}X rewards on orders with a subtotal over $${formatNumberWithCommas(planDetails[0].minimumSaleInCents / 100, { asCurrency: true })}`}
              </div>
            ) : planDetails.length > 1 ? (
              planDetails.map((plan, index) => (
                <div key={index} style={index < planDetails.length - 1 ? { marginBottom: 6 } : undefined}>
                  {index === planDetails.length - 1
                    ? `• Earn ${plan.cashbackMultiple_100 / 100}X rewards on orders with a subtotal over $${formatNumberWithCommas(plan.minimumSaleInCents / 100, { asCurrency: true })}`
                    : index === 0 && plan.minimumSaleInCents <= 100
                    ? `• Earn ${plan.cashbackMultiple_100 / 100}X rewards on orders with a subtotal up to $${formatNumberWithCommas(plan.maximumSaleInCents / 100, { asCurrency: true })}`
                    : `• Earn ${plan.cashbackMultiple_100 / 100}X rewards on orders with a subtotal from $${formatNumberWithCommas(plan.minimumSaleInCents / 100, { asCurrency: true })} to $${formatNumberWithCommas(plan.maximumSaleInCents / 100, { asCurrency: true })}`}
                </div>
              ))
            ) : (
              "No cashback tiers available"
            )}
          </div>
        }
        placement="bottom"
      >
        <MDAvatar src={tooltipIcon} name={"maxCashback"} size="sm" sx={{ width: 15, height: 15 }} />
      </Tooltip>
    </MDBox>
  );
}

export function buildStoreRow(storeData) {
  const progressData = storeData.progressData || {};
  const store = storeData._restaurant_store_restaurant_store;
  if (!store) return null;
  return {
    restaurantName: (
      <MDBox
        display="flex"
        alignItems="center"
        lineHeight={1}
        component="a"
        onClick={async () => {
          let orderNowUrl = await getRestaurantPageDetails(store.restaurantPrefix);
          if (orderNowUrl) {
            window.open(orderNowUrl, "_blank");
          } else {
            window.open(
              `${
                config[process.env.REACT_APP_ENV?.toUpperCase() || "LOCAL"]
                  .API_LANDING_PAGE_BASE_URL
              }/restaurants/${store.restaurantPrefix}`,
              "_blank"
            );
          }
        }}
        target="_blank"
        sx={{ cursor: "pointer" }}
      >
        <MDTypography variant="button" fontWeight="medium" lineHeight={1}>
          {store.name}
        </MDTypography>
      </MDBox>
    ),
    location: (
      <MDTypography
        variant="caption"
        color="text"
        fontWeight="medium"
        sx={{
          display: "block",
          maxWidth: "150px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {store.storeAddressLine1 || "-"}
      </MDTypography>
    ),
    last12Months: (
      <MDBox sx={{ display: "flex", gap: 1 }}>
        <MDTypography variant="caption" color="text" fontWeight="medium">
          $
          {storeData?.totalSpendSinceAnniversary
            ? formatNumberWithCommas(storeData?.totalSpendSinceAnniversary / 100, { asCurrency: true })
            : 0}
        </MDTypography>
        <Tooltip
          key={"amount-reset-info"}
          title={
            <div style={{ textAlign: "left" }}>
              This amount resets on your anniversary date each year.
            </div>
          }
          placement="bottom"
        >
          <MDAvatar
            src={tooltipIcon}
            name={"amount-reset-info"}
            size="sm"
            sx={{ width: 15, height: 15 }}
          />
        </Tooltip>
      </MDBox>
    ),
    anniversaryDate: (
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {formatDateInTimezone(storeData.anniversaryDate, null, "longMonth")}
      </MDTypography>
    ),
    maxCashback: buildMaxCashbackCell(
      store._loyalty_plan_set_loyalty_plan_set?.loyalty_plans,
      storeData.currentLevel,
      store.restaurantPrefix
    ),
    progress: buildProgressCell(progressData, storeData.currentLevel),
  };
}

export default function data({ topRestaurantData }) {
  return {
    columns: [
      { Header: "RESTAURANT NAME", accessor: "restaurantName", width: "20%", align: "left" },
      { Header: "LOCATION", accessor: "location", align: "left" },
      { Header: "ORDERS SINCE ANNIVERSARY", accessor: "last12Months", align: "center" },
      { Header: "ANNIVERSARY DATE", accessor: "anniversaryDate", align: "center" },
      {
        Header: (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            MAX CASHBACK
            <Tooltip title="Max Cashback is based on your current tier" placement="bottom">
              <img src={tooltipIcon} alt="info" style={{ width: 15, height: 15 }} />
            </Tooltip>
          </span>
        ),
        accessor: "maxCashback",
        align: "center",
      },
      {
        Header: "CURRENT TIER",
        accessor: "progress",
      },
    ],

    rows: topRestaurantData?.map((storeData) => buildStoreRow(storeData)).filter(Boolean),
  };
}

data.PropTypes = {
  topRestaurantData: PropTypes.any,
};
